
/* ============================================================
   HERRADURA DE ORO — THEME.JS
   Product/cart, variants, swatches, mobile menu, header, utilities
   Product page Add to Cart redirects to the full /cart page.
   ============================================================ */
'use strict';

const HDO = {
  root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  },
  route(path) {
    return this.root().replace(/\/$/, '/') + path.replace(/^\//, '');
  },
  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },
  async getJSON(url, opts = {}) {
    const response = await fetch(url, {
      headers: { Accept: 'application/json', ...(opts.headers || {}) },
      ...opts
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || `HTTP ${response.status}`);
    return data;
  },
  postJSON(url, data) {
    return this.getJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    });
  },
  on(element, event, handler) {
    if (element) element.addEventListener(event, handler);
  },
  delegate(parent, selector, event, handler) {
    if (!parent) return;
    parent.addEventListener(event, e => {
      const target = e.target.closest(selector);
      if (target && parent.contains(target)) handler(e, target);
    });
  },
  focusTrap: null,
  trapFocus(element) {
    const focusable = Array.from(element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    if (!focusable.length) return;
    this.releaseFocus();
    this.focusTrap = { element, handler: e => {
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }};
    element.addEventListener('keydown', this.focusTrap.handler);
    focusable[0].focus();
  },
  releaseFocus() {
    if (!this.focusTrap) return;
    this.focusTrap.element.removeEventListener('keydown', this.focusTrap.handler);
    this.focusTrap = null;
  }
};

/* Drawer support is retained for any other theme triggers. */
const Cart = {
  drawer: null, overlay: null, body: null, footer: null, isOpen: false,
  init() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.body = document.getElementById('cart-drawer-body');
    this.footer = document.getElementById('cart-drawer-footer');
    HDO.delegate(document, '[data-open-cart]', 'click', (e) => {
      e.preventDefault(); this.open();
    });
    HDO.on(document.getElementById('cart-drawer-close'), 'click', () => this.close());
    HDO.on(this.overlay, 'click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    HDO.delegate(this.body, '[data-remove-item]', 'click', (e, el) => {
      e.preventDefault(); this.updateItem(el.dataset.removeItem, 0);
    });
    HDO.delegate(this.body, '[data-qty-btn]', 'click', (e, el) => {
      e.preventDefault();
      const key = el.dataset.qtyBtn;
      const input = Array.from(this.body.querySelectorAll('[data-qty-input]'))
        .find(node => node.dataset.qtyInput === key);
      if (!input) return;
      const next = Math.max(0, (parseInt(input.value, 10) || 0) + (el.dataset.dir === 'up' ? 1 : -1));
      this.updateItem(key, next);
    });
  },
  async open() {
    if (!this.drawer || !this.overlay || !this.body || !this.footer) {
      window.location.assign(HDO.route('cart'));
      return;
    }
    await this.render();
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    HDO.trapFocus(this.drawer);
  },
  close() {
    if (!this.drawer || !this.overlay) return;
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.isOpen = false;
    HDO.releaseFocus();
  },
  async render() {
    if (!this.body || !this.footer) return;
    this.body.textContent = 'Loading cart...';
    this.footer.textContent = '';
    try {
      const cart = await HDO.getJSON(HDO.route('cart.js'));
      this.renderItems(cart);
      this.renderFooter(cart);
      this.updateCount(cart.item_count);
    } catch (error) {
      console.error('Cart load error:', error);
      this.body.textContent = 'Could not load cart.';
    }
  },
  renderItems(cart) {
    if (!this.body) return;
    this.body.replaceChildren();
    if (!cart.items.length) {
      const empty = document.createElement('div');
      empty.className = 'cart-drawer__empty';
      const message = document.createElement('p');
      message.textContent = 'Your cart is empty.';
      const link = document.createElement('a');
      link.href = HDO.route('collections/all');
      link.className = 'btn btn--primary';
      link.textContent = 'Continue Shopping';
      empty.append(message, link);
      this.body.appendChild(empty);
      return;
    }
    cart.items.forEach(item => {
      const row = document.createElement('div');
      row.className = 'cart-item';
      row.dataset.key = item.key;
      if (item.featured_image && item.featured_image.url) {
        const img = document.createElement('img');
        img.className = 'cart-item__image';
        img.src = item.featured_image.url;
        img.alt = item.product_title || '';
        img.loading = 'lazy';
        row.appendChild(img);
      }
      const details = document.createElement('div');
      details.className = 'cart-item__details';
      const title = document.createElement('div');
      title.className = 'cart-item__title';
      title.textContent = item.product_title;
      details.appendChild(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('div');
        variant.className = 'cart-item__variant';
        variant.textContent = item.variant_title;
        details.appendChild(variant);
      }
      const quantity = document.createElement('div');
      quantity.className = 'quantity-input';
      quantity.style.margin = '8px 0';
      const down = document.createElement('button');
      down.type = 'button'; down.dataset.qtyBtn = item.key;
      down.dataset.dir = 'down'; down.setAttribute('aria-label', 'Decrease quantity');
      down.textContent = '−';
      const input = document.createElement('input');
      input.type = 'number'; input.value = item.quantity; input.min = '0';
      input.readOnly = true; input.dataset.qtyInput = item.key;
      const up = document.createElement('button');
      up.type = 'button'; up.dataset.qtyBtn = item.key;
      up.dataset.dir = 'up'; up.setAttribute('aria-label', 'Increase quantity');
      up.textContent = '+';
      quantity.append(down, input, up);
      details.appendChild(quantity);
      const price = document.createElement('div');
      price.className = 'cart-item__price';
      price.textContent = HDO.formatMoney(item.final_line_price);
      details.appendChild(price);
      const remove = document.createElement('button');
      remove.type = 'button'; remove.className = 'cart-item__remove';
      remove.dataset.removeItem = item.key; remove.textContent = 'Remove';
      details.appendChild(remove);
      row.appendChild(details);
      this.body.appendChild(row);
    });
  },
  renderFooter(cart) {
    if (!this.footer) return;
    this.footer.replaceChildren();
    if (!cart.items.length) return;
    const subtotal = document.createElement('div');
    subtotal.className = 'cart-drawer__subtotal';
    const label = document.createElement('span'); label.textContent = 'Subtotal';
    const amount = document.createElement('span'); amount.textContent = HDO.formatMoney(cart.total_price);
    subtotal.append(label, amount);
    const checkout = document.createElement('a');
    checkout.href = HDO.route('checkout');
    checkout.className = 'btn btn--primary btn--full';
    checkout.textContent = 'Checkout';
    const view = document.createElement('a');
    view.href = HDO.route('cart');
    view.className = 'btn btn--outline-dark btn--full';
    view.style.marginTop = '8px'; view.textContent = 'View Cart';
    const note = document.createElement('p');
    note.className = 'cart-drawer__note';
    note.textContent = 'Shipping & taxes calculated at checkout';
    this.footer.append(subtotal, checkout, view, note);
  },
  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
      el.setAttribute('aria-label', `${count} items in cart`);
    });
    document.body.dataset.cartCount = count;
  },
  async addItem(data) {
    const result = await HDO.postJSON(HDO.route('cart/add.js'), data);
    const cart = await HDO.getJSON(HDO.route('cart.js'));
    this.updateCount(cart.item_count);
    return result;
  },
  async updateItem(key, quantity) {
    try {
      const cart = await HDO.postJSON(HDO.route('cart/change.js'), { id: key, quantity });
      this.renderItems(cart); this.renderFooter(cart); this.updateCount(cart.item_count);
    } catch (error) {
      console.error('Cart update error:', error);
      await this.render();
    }
  },
  removeItem(key) { return this.updateItem(key, 0); }
};

/* Product form submission: add selected variant and open full /cart page. */
const AddToCart = {
  init() {
    document.addEventListener('submit', async event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;
      event.preventDefault();
      if (form.dataset.submitting === 'true') return;
      const idInput = form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) {
        alert('Please select a product option.'); return;
      }
      const button = form.querySelector('[data-add-btn]') || form.querySelector('[name="add"]');
      const label = form.querySelector('[data-add-btn-text]');
      const original = label ? label.textContent : (button ? button.textContent : '');
      form.dataset.submitting = 'true';
      if (button) button.disabled = true;
      if (label) label.textContent = 'Adding...';
      try {
        const response = await fetch(HDO.route('cart/add.js'), {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add product to cart.');
        window.location.assign(HDO.route('cart'));
      } catch (error) {
        console.error('Add to Cart error:', error);
        alert(error.message || 'Could not add item to cart.');
        form.dataset.submitting = 'false';
        if (button) button.disabled = false;
        if (label) label.textContent = original;
      }
    });
  }
};

const Variants = {
  init() {
    document.querySelectorAll('[data-variant-form]').forEach(form => this.bindForm(form));
  },
  bindForm(form) {
    const json = form.querySelector('[data-product-json]');
    if (!json) return;
    let product;
    try { product = JSON.parse(json.textContent); }
    catch (error) { console.error('Invalid product JSON:', error); return; }
    const options = Array.from(form.querySelectorAll('[data-option-select]'));
    const priceEl = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare-price]');
    const addBtn = form.querySelector('[data-add-btn]');
    const addText = form.querySelector('[data-add-btn-text]');
    const variantId = form.querySelector('[name="id"]');
    const gallery = document.querySelector('[data-product-gallery]');
    const update = () => {
      const values = options.map(select => select.value);
      const variant = product.variants.find(v => v.options.every((opt, i) => opt === values[i]));
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addText) addText.textContent = 'Unavailable';
        return;
      }
      if (variantId) variantId.value = variant.id;
      if (priceEl) priceEl.textContent = HDO.formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = HDO.formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else compareEl.style.display = 'none';
      }
      if (addBtn) addBtn.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      if (variant.featured_image && gallery) {
        const image = gallery.querySelector('[data-main-image]');
        if (image) {
          image.src = variant.featured_image.src;
          image.srcset = '';
        }
      }
    };
    options.forEach(select => HDO.on(select, 'change', update));
    update();
  }
};

const Swatches = {
  init() {
    document.querySelectorAll('[data-option-select]').forEach(select => this.buildSwatches(select));
  },
  buildSwatches(select) {
    if (select.dataset.swatchesReady === 'true') return;
    select.dataset.swatchesReady = 'true';
    const group = document.createElement('div');
    group.className = 'swatch-group';
    group.setAttribute('aria-label', select.getAttribute('aria-label') || 'Product options');
    const buttons = [];
    Array.from(select.options).forEach(option => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'swatch-btn';
      button.textContent = option.value;
      button.dataset.value = option.value;
      button.classList.toggle('is-selected', option.selected);
      button.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      group.appendChild(button);
      buttons.push(button);
    });
    select.addEventListener('change', () => buttons.forEach(button => {
      button.classList.toggle('is-selected', button.dataset.value === select.value);
    }));
    // Keep the native select available to assistive technology.
    select.style.position = 'absolute';
    select.style.width = '1px';
    select.style.height = '1px';
    select.style.opacity = '0';
    select.style.pointerEvents = 'none';
    select.after(group);
  }
};

const MobileMenu = {
  menu: null, overlay: null, isOpen: false,
  init() {
    this.menu = document.getElementById('mobile-menu');
    this.overlay = document.getElementById('mobile-overlay');
    HDO.delegate(document, '[data-open-menu]', 'click', (e) => { e.preventDefault(); this.open(); });
    HDO.on(document.getElementById('mobile-menu-close'), 'click', () => this.close());
    HDO.on(this.overlay, 'click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  },
  open() {
    if (!this.menu || !this.overlay) return;
    this.menu.classList.add('is-open'); this.overlay.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; this.isOpen = true;
    HDO.trapFocus(this.menu);
  },
  close() {
    if (!this.menu || !this.overlay) return;
    this.menu.classList.remove('is-open'); this.overlay.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = ''; this.isOpen = false;
    HDO.releaseFocus();
  }
};

const StickyHeader = {
  header: null, lastY: 0,
  init() {
    this.header = document.getElementById('site-header');
    if (this.header) window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  },
  onScroll() {
    const y = window.scrollY;
    this.header.classList.toggle('is-scrolled', y > 80);
    this.header.classList.toggle('is-hidden', y > this.lastY && y > 200);
    this.lastY = y;
  }
};

const Gallery = {
  init() {
    const gallery = document.querySelector('[data-product-gallery]');
    HDO.delegate(gallery, '[data-thumb]', 'click', (e, thumb) => {
      e.preventDefault();
      const image = gallery.querySelector('[data-main-image]');
      if (image) {
        image.src = thumb.dataset.src;
        image.srcset = thumb.dataset.srcset || '';
      }
      gallery.querySelectorAll('[data-thumb]').forEach(el => el.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  }
};

const AnnouncementBar = {
  init() {
    const btn = document.querySelector('[data-dismiss-announcement]');
    const bar = document.getElementById('announcement-bar');
    if (!btn || !bar) return;
    const key = 'hdo_ann_dismissed';
    try { if (sessionStorage.getItem(key)) { bar.style.display = 'none'; return; } }
    catch (error) { /* Storage might be disabled. */ }
    HDO.on(btn, 'click', () => {
      bar.style.display = 'none';
      try { sessionStorage.setItem(key, '1'); } catch (error) { /* Ignore. */ }
    });
  }
};

/* Collection page currently initializes its own filter and sort controls.
   Avoid installing competing click/change handlers from theme.js. */
const CollectionFilter = { init() {} };

const QuantitySteppers = {
  init() {
    HDO.delegate(document, '[data-qty-change]', 'click', (e, btn) => {
      e.preventDefault();
      const target = document.querySelector(btn.dataset.qtyChange);
      if (!target) return;
      const direction = btn.dataset.dir;
      const minimum = parseInt(target.min || '1', 10);
      const value = parseInt(target.value || '1', 10);
      target.value = direction === 'up' ? value + 1 : Math.max(minimum, value - 1);
      target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
};

const SmoothScroll = {
  init() {
    HDO.delegate(document, 'a[href^="#"]', 'click', (e, link) => {
      const id = link.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
};

const BackToTop = {
  init() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    window.addEventListener('scroll', () => {
      btn.classList.toggle('is-visible', window.scrollY > 500);
    }, { passive: true });
    HDO.on(btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }
};

document.addEventListener('DOMContentLoaded', () => {
  Cart.init();
  AddToCart.init();
  Variants.init();
  Swatches.init();
  MobileMenu.init();
  StickyHeader.init();
  Gallery.init();
  AnnouncementBar.init();
  CollectionFilter.init();
  QuantitySteppers.init();
  SmoothScroll.init();
  BackToTop.init();
});
